import { APP_INITIALIZER, ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { RouterModule, Routes } from '@angular/router';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';

import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { MissingTranslationHandler, TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastContainerComponent } from '@core/components/ui/toast-container.component';
import { QuillModule } from 'ngx-quill';
import Quill from 'quill';

const Font = Quill.import('formats/font') as any;
Font.whitelist = ['poppins', 'arial', 'georgia', 'times-new-roman', 'courier-new', 'montserrat'];
Quill.register(Font, true);

import { CoreModule } from '@core/core.module';
import { CoreCommonModule } from '@core/common.module';
import { CoreSidebarModule, CoreTableModule } from '@core/components';

import { coreConfig } from 'app/app-config';

import { AppComponent } from 'app/app.component';
import { LayoutModule } from 'app/layout/layout.module';
import { AuthTokenInterceptor, DialogService, ErrorInterceptor } from './server/helpers';
import { DatePipe } from '@angular/common';
import { DEFAULT_LANGUAGE } from 'app/config/languages.config';
import { Utils } from 'app/custom-class/utils';
import { UniversityInterceptor } from './server/helpers/university.interceptor';
import { ApiUrlInterceptor } from './server/helpers/api-url.interceptor';
import { ContentHeaderModule } from './layout/components/content-header/content-header.module';
import { UniversityService } from './server/services/university.service';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { VocabularyService } from './server/services/vocabulary.service';
import { ProfileInterceptor } from './server/helpers/profile.interceptor';
import { CurrentUniversityService } from './server/services/current-university.service';
import { CareerInterceptor } from './server/helpers/career.interceptor';
import { CustomMissingTranslationHandler } from './custom-class/custom-missing-translation-handler';
import { translationInitializer } from './initializers/translation.initializer';
import { universityDomainInitializer } from './initializers/university-domain.initializer';
import { AddonGuard } from './server/helpers/addon.guard';
import { GlobalErrorHandler } from './server/helpers/global-error.handler';


class DynamicLocaleId extends String {
	constructor(private _translateService: TranslateService) {
		super(DEFAULT_LANGUAGE.localeId);
	}

	override toString(): string {
		const lang = this._translateService?.currentLang || this._translateService?.defaultLang || DEFAULT_LANGUAGE.code;
		return Utils.getAppLanguage(lang).localeId;
	}

	override valueOf(): string {
		return this.toString();
	}
}

const appRoutes: Routes = [
	{
		path: 'internship-secretary',
		canActivate: [AddonGuard],
		data: { requiredAddon: 'Tirocini' },
		loadChildren: () => import('./modules/internship-secretary/internship-secretary.module').then(m => m.InternshipSecretaryModule),
	},
	{
		path: 'student',
		canActivate: [AddonGuard],
		data: { requiredAddon: 'Tirocini' },
		loadChildren: () => import('./modules/student/student.module').then(m => m.StudentModule),
	},
	{
		path: 'tutor',
		canActivate: [AddonGuard],
		data: { requiredAddon: 'Tirocini' },
		loadChildren: () => import('./modules/tutor/tutor.module').then(m => m.TutorModule),
	},
	{
		path: '',
		loadChildren: () => import('./main/pages/pages.module').then(m => m.PagesModule),
	},
	{
		path: 'pages',
		redirectTo: '/home',
		pathMatch: 'full',
	},
	{
		path: '**',
		redirectTo: '/miscellaneous/error', // Error 404 - Page not found
	},
];

export class CustomTranslateHttpLoader implements TranslateLoader {
	constructor(private _http: HttpClient, private _prefix: string, private _suffix: string) {}

	public getTranslation(lang: string): Observable<any> {
		return this._http.get<any>(`${this._prefix}${lang}${this._suffix}`).pipe(
			map(res => res?.data || res),
			catchError(() => {
				// Backend has no data/route for this language (e.g. not yet supported):
				// fall back to the default language instead of leaving the app without translations.
				if (lang === DEFAULT_LANGUAGE.code) {
					return [{}];
				}
				return this._http.get<any>(`${this._prefix}${DEFAULT_LANGUAGE.code}${this._suffix}`).pipe(map(res => res?.data || res));
			})
		);
	}
}

export function HttpLoaderFactory(http: HttpClient) {
	return new CustomTranslateHttpLoader(http, '/vocabularies/', '/keyed');
}

@NgModule({
	declarations: [AppComponent],
	providers: [
		{ provide: ErrorHandler, useClass: GlobalErrorHandler },
		DialogService,
		UniversityService,
		DatePipe,
		CurrentUniversityService,
		{
			provide: LOCALE_ID,
			useFactory: (translate: TranslateService) => new DynamicLocaleId(translate),
			deps: [TranslateService],
		},
		{
			provide: APP_INITIALIZER,
			useFactory: universityDomainInitializer,
			deps: [UniversityService, CurrentUniversityService],
			multi: true,
		},
		{
			provide: APP_INITIALIZER,
			useFactory: translationInitializer,
			deps: [TranslateService, VocabularyService],
			multi: true,
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: ApiUrlInterceptor,
			multi: true,
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: UniversityInterceptor,
			multi: true,
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: ProfileInterceptor,
			multi: true,
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: CareerInterceptor,
			multi: true,
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: AuthTokenInterceptor,
			multi: true,
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: ErrorInterceptor,
			multi: true,
		},
	],
	imports: [
		BrowserModule,
		BrowserAnimationsModule,
		HttpClientModule,
		RouterModule.forRoot(appRoutes, {
			scrollPositionRestoration: 'enabled', // Add options right here
			// relativeLinkResolution: 'legacy',
			onSameUrlNavigation: 'reload',
		}),
		TranslateModule.forRoot({
			missingTranslationHandler: {
				provide: MissingTranslationHandler,
				useClass: CustomMissingTranslationHandler,
				deps: [VocabularyService],
			},
			loader: {
				provide: TranslateLoader,
				useFactory: HttpLoaderFactory,
				deps: [HttpClient],
			},
		}),

		// NgBootstrap
		NgbModule,
		ToastContainerComponent,

		// Core modules
		CoreModule.forRoot(coreConfig),
		CoreCommonModule,
		CoreSidebarModule,

		// App modules
		LayoutModule,
		ContentHeaderModule,
		CoreTableModule,
		QuillModule.forRoot({
			modules: {
				toolbar: [
					['bold', 'italic', 'underline', 'strike'],
					['blockquote', 'code-block'],
					[{ 'header': 1 }, { 'header': 2 }],
					[{ 'list': 'ordered'}, { 'list': 'bullet' }],
					[{ 'script': 'sub'}, { 'script': 'super' }],
					[{ 'indent': '-1'}, { 'indent': '+1' }],
					[{ 'direction': 'rtl' }],
					[{ 'size': ['small', false, 'large', 'huge'] }],
					[{ 'header': [1, 2, 3, 4, 5, 6, false] }],
					[{ 'color': [] }, { 'background': [] }],
					[{ 'align': [] }],
					['clean'],
					['link', 'image', 'video']
				]
			}
		}),
	],

	bootstrap: [AppComponent],
})
export class AppModule {}
